<template>
  <b-overlay
    :show="loading"
    rounded="sm"
    spinner-type="grow"
    spinner-variant="warning"
  >
    <b-button squared class="buttons" variant="light">
      <b-icon scale="2" icon="x" id="idCaseClose" @click="onClose" />
    </b-button>
    <b-jumbotron bg-variant="light">
      <h4 style="text-align: left">
        <strong>Create Surgical Case</strong>
      </h4>
      <b-form @submit="onSubmit">
        <b-row align-h="between" class="mb-2">
          <b-col style="margin-right: 1rem">
            <b-form-row class="mb-2">
              <label class="required" for="idDivision">Division</label>
              <b-form-select
                size="sm"
                required
                id="idDivision"
                v-model="selectedDivision"
                :options="divisionList"
                disabled
              ></b-form-select>
            </b-form-row>
            <b-form-row class="mb-2">
              <label class="required" for="idBranch">Branch</label>
              <b-form-select
                size="sm"
                required
                id="idBranch"
                v-model="selectedBranch"
                :options="branchList"
                :disabled="disabledState"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null" disabled
                    >-- Please select --</b-form-select-option
                  >
                </template>
              </b-form-select>
            </b-form-row>
            <b-form-row class="mb-2">
              <label class="required" for="idHospital">Hospital</label>
              <b-form-input
                size="sm"
                required
                lazy
                list="hospitalList"
                id="idHospital"
                autocomplete="off"
                v-model="selectedHospital"
                placeholder="search..."
                :state="hospitalState"
                @blur="checkHospitalValid"
              ></b-form-input>
              <b-form-datalist
                id="hospitalList"
                :options="hospitalListModified"
              ></b-form-datalist>
            </b-form-row>

            <b-form-row class="mb-2">
              <label class="required" for="idSurgeon">Surgeon</label>
              <b-form-input
                size="sm"
                required
                list="surgeonList"
                id="idSurgeon"
                autocomplete="off"
                v-model="selectedSurgeon"
                placeholder="search..."
                :state="surgeonState"
                @blur="checkSurgeonValid"
              ></b-form-input>
              <b-form-datalist
                id="surgeonList"
                :options="surgeonListModified"
              ></b-form-datalist>
            </b-form-row>

            <b-form-row class="mb-2">
              <label class="required" for="idCoveringReps">Covering Reps</label>
              <b-form-select
                size="sm"
                id="idCoveringReps"
                v-model="selectedCoveringReps"
                multiple
                required
                :options="coveringRepListModified"
                :select-size="6"
              ></b-form-select>
            </b-form-row>

            <b-form-row class="mb-2">
              <label class="required" for="idProcedure">Procedure</label>
              <b-form-select
                size="sm"
                required
                id="idProcedure"
                v-model="selectedProcedure"
                :options="procedureList"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null" disabled
                    >-- Please select --</b-form-select-option
                  >
                </template>
              </b-form-select>
            </b-form-row>
            <b-form-row class="mb-2">
              <b-col cols="6" lg="4">
                <label class="required">Surgery Start Date</label>
                <b-input-group class="mb-3">
                  <b-form-input
                    id="idSurgeryStartDateInput"
                    required
                    autocomplete="off"
                    v-model="surgeryDateInput"
                    type="text"
                    size="sm"
                    @blur="onSurgeryDateBlur"
                    :state="surgeryDateState"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-form-datepicker
                      show-decade-nav
                      id="idSurgeryStartDateBtn"
                      v-model="selectedSurgeryDate"
                      button-only
                      button-variant="outline-dark"
                      right
                      size="sm"
                      aria-controls="idSurgeryStartDateInput"
                      @context="onSurgeryDateContext"
                    ></b-form-datepicker>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
              <b-col cols="6" lg="4">
                <label class="required">Surgery Start Time</label>
                <b-input-group class="mb-3">
                  <b-form-input
                    id="idSurgeryStartTimeInput"
                    v-model="surgeryTimeInput"
                    type="text"
                    required
                    autocomplete="off"
                    size="sm"
                    @blur="onSurgeryTimeBlur"
                    :state="surgeryTimeState"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-form-timepicker
                      id="idSurgeryStartTimeBtn"
                      minutes-step="15"
                      v-model="selectedSurgeryTime"
                      button-only
                      close-button-variant="primary"
                      button-variant="outline-dark"
                      right
                      size="sm"
                      locale="en"
                      aria-controls="idSurgeryStartTimeInput"
                      @context="onSurgeryTimeContext"
                    ></b-form-timepicker>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
            </b-form-row>
          </b-col>
          <b-col style="margin-left: 1rem">
            <b-form-row class="mb-2">
              <label for="idPatientId">Patient Id</label>
              <b-form-input
                size="sm"
                id="idPatientId"
                autocomplete="off"
                v-model="patientId"
              ></b-form-input>
            </b-form-row>

            <b-form-row class="mb-2">
              <b-form-group label="Operative Side" style="text-align: left">
                <b-form-checkbox-group
                  id="idOperativeSide"
                  @change="onOperativeSideInput"
                  v-model="selectedOperativeSide"
                  :options="operativeSideOptions"
                ></b-form-checkbox-group>
              </b-form-group>
            </b-form-row>

            <b-form-row class="mb-2">
              <b-form-group label="Mako" style="text-align: left">
                <b-form-checkbox
                  disabled
                  id="idMako"
                  style="float: left"
                  v-model="mako"
                />
              </b-form-group>
            </b-form-row>

            <b-form-row class="mb-2">
              <b-form-group label="Revision" style="text-align: left">
                <b-form-checkbox
                  id="idRevision"
                  v-model="revision"
                  style="float: left"
                />
              </b-form-group>
            </b-form-row>

            <b-form-row class="mb-2">
              <label for="idInternalComments">Internal Comments</label>
              <b-form-textarea
                id="idInternalComments"
                v-model="internalComments"
                rows="2"
                max-rows="2"
                :disabled="disabledState"
              ></b-form-textarea>
              <div class="red">
                Please do not enter complaints in this text box.
              </div>
            </b-form-row>

            <b-form-row class="mb-2">
              <b-button
                id="idSubmitBtn"
                class="buttons"
                type="submit"
                variant="primary"
                >Save</b-button
              >
            </b-form-row>
          </b-col>
        </b-row>
      </b-form>
    </b-jumbotron>
  </b-overlay>
</template>

<script>
// RHS imports
import { SurgicalCaseDto, ContactDto } from "rhs-surgical-case-api-v2";

// Others
import moment from "moment";

export default {
  data() {
    return {
      hospitalState: null,
      surgeonState: null,
      surgeryTimeState: null,
      surgeryDateState: null,
      surgeryTimeInput: null,
      surgeryDateInput: null,
      surgicalCaseId: null,
      status: null,
      mako: true,
      revision: false,
      divisionList: ["Joint Replacement"],
      branchList: [""],
      selectedDivision: "Joint Replacement",
      selectedBranch: null,
      selectedHospital: "",
      selectedSurgeon: "",
      selectedOperativeSide: [],
      operativeSideOptions: [
        { text: "Left", value: "left" },
        { text: "Right", value: "right" },
      ],
      disabled: true,
      internalComments: "",
      selectedCoveringReps: [],
      procedureList: [
        "MAKO - PKA",
        "MAKO - THA",
        "MAKO - TKA Bilateral",
        "MAKO - TKA Left",
        "MAKO - TKA Right",
      ],
      selectedProcedure: null,
      selectedSurgeryDate: null,
      selectedSurgeryTime: null,
      patientId: null,
      treatmentPlanRequests: [],
      loading: true,
      hospitalList: [],
      hospitalListModified: [],
      surgeonList: [],
      surgeonListModified: [],
      coveringRepList: [],
      coveringRepListModified: [],
    };
  },
  watch: {
    async selectedBranch() {
      // Update Hospitals after branch changed
      const hospitalsResponse = await this.$buhApi.branchesIdHospitalsGet(
        this.selectedBranch
      );
      this.hospitalList = hospitalsResponse;
      this.hospitalListModified = hospitalsResponse.map(
        (hospital) =>
          // remove all duplicate spaces! else this will screw up visualization
          hospital.externalReference.trim() +
          " - " +
          hospital.hospitalName.replace(/\s\s+/g, " ").trim()
      );

      // Update Surgeons after branch changed
      const surgeonsResponse = await this.$buhApi.branchesIdSurgeonsGet(
        this.selectedBranch
      );
      this.surgeonList = surgeonsResponse;
      this.surgeonListModified = surgeonsResponse.map(
        (surgeon) =>
          // remove all trailing spaces! else this will screw up visualization
          surgeon.externalReference.trim() +
          " - " +
          surgeon.firstName.trim() +
          " " +
          surgeon.lastName.trim()
      );

      // Update Covering Rep after branch changed
      const usersResponse = await this.$buhApi.branchesIdUsersGet(
        this.selectedBranch
      );
      this.coveringRepList = usersResponse;
      this.coveringRepListModified = usersResponse.map((user) => {
        return {
          // remove all trailing spaces! else this will screw up visualization
          text: user.firstName.trim() + " " + user.lastName.trim(),
          value: user.userEmail,
        };
      });
    },
  },
  computed: {
    disabledState() {
      return this.surgicalCaseId ? true : false;
    },
  },
  async created() {
    this.surgicalCaseId = this.$route.query.id;
    this.loading = true;

    // Retrieve all Lists from BUH-Service
    try {
      const branchesResponse = await this.$buhApi.branchesUsersGet(
        this.$msal.data.user.userName
      );
      this.branchList = branchesResponse.branches.map((branch) => {
        return {
          text: branch.externalReference + " - " + branch.name,
          value: branch.entityId,
        };
      });
    } catch (e) {
      this.$root.$emit("showAlert", {
        type: "error",
        msg: `The user "${this.$msal.data.user.userName}" is not set up yet, please contact your admin.`,
      });
      this.loading = false;
      return;
    }
    // If we are in edit mode, retrieve all data
    if (this.surgicalCaseId) {
      const caseResponse = await this.$surgicalCaseApi.surgicalcasesIdGet(
        this.surgicalCaseId
      );

      const selectedBranch = this.branchList.filter((branch) => {
        if (
          branch.text.trim() ===
          caseResponse.branchNumber.trim() +
            " - " +
            caseResponse.branchName.trim()
        )
          return branch.text;
      })[0];

      this.selectedBranch = selectedBranch.value;
      this.selectedDivision = caseResponse.division;
      this.selectedHospital =
        caseResponse.hospitalNumber.trim() +
        " - " +
        caseResponse.hospitalName.trim();
      this.selectedSurgeon =
        caseResponse.surgeon.email +
        " - " +
        caseResponse.surgeon.firstName +
        " " +
        caseResponse.surgeon.lastName;
      this.selectedOperativeSide = [caseResponse.operativeSide];
      this.selectedProcedure = caseResponse.procedure;
      this.selectedSurgeryDate = moment(
        caseResponse.surgeryStartDateTime
      ).format("YYYY-MM-DD");
      this.selectedSurgeryTime = moment(
        caseResponse.surgeryStartDateTime
      ).format("HH:mm");

      this.patientId = caseResponse.patientId;
      this.mako = caseResponse.mako;
      this.revision = caseResponse.revision;
      caseResponse.coveringReps.forEach((rep) => {
        this.selectedCoveringReps.push(rep.email);
      });
      this.treatmentPlanRequests = caseResponse.treatmentPlanRequests;
      this.status = caseResponse.metadata.commentedCaseState.surgicalCaseState;
      this.internalComments = caseResponse.internalComments;
      // Enable Form
    }
    this.loading = false;
  },
  methods: {
    // Provides feedback immediately to user if hospital typed in is valid
    checkHospitalValid() {
      if (this.hospitalListModified.includes(this.selectedHospital)) {
        this.hospitalState = null;
      } else {
        this.hospitalState = false;
      }
    },
    // Provides feedback immediately to user if surgeon typed in is valid
    checkSurgeonValid() {
      if (this.surgeonListModified.includes(this.selectedSurgeon)) {
        this.surgeonState = null;
      } else {
        this.surgeonState = false;
      }
    },
    onOperativeSideInput(input) {
      if (input.length > 1) this.selectedOperativeSide = [input[1]];
    },
    onSurgeryDateBlur() {
      if (
        this.surgeryDateInput &&
        this.surgeryDateInput.match(/[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4}/g)
      ) {
        this.selectedSurgeryDate = moment(this.surgeryDateInput).format(
          "YYYY-M-D"
        );
        if (!this.selectedSurgeryTime) {
          this.selectedSurgeryTime = "06:00:00";
          this.surgeryTimeState = null;
        }
        this.surgeryDateState = null;
      } else {
        // Set the state to false to tell user that the date is in the wrong format
        this.surgeryDateState = false;
      }
    },
    onSurgeryDateContext(ctx) {
      if (ctx.selectedDate) {
        this.surgeryDateInput = moment(ctx.selectedDate).format("M/D/YYYY");
        if (!this.selectedSurgeryTime) {
          this.selectedSurgeryTime = "06:00:00";
          this.surgeryTimeState = null;
        }
        this.surgeryDateState = null;
      }
    },
    onSurgeryTimeBlur() {
      if (
        this.surgeryTimeInput &&
        this.surgeryTimeInput.match(/[0-9]{1,2}:[0-9]{1,2}/g)
      ) {
        this.surgeryTimeInput = moment(this.surgeryTimeInput, "LT").format(
          "LT"
        );
        this.selectedSurgeryTime = moment(this.surgeryTimeInput, "LT").format(
          "HH:mm:ss"
        );
        this.surgeryTimeState = null;
      } else {
        // Set the state to false to tell user that the time is in the wrong format
        this.surgeryTimeState = false;
      }
    },
    onSurgeryTimeContext(ctx) {
      if (ctx.hours !== null && ctx.minutes !== null) {
        this.surgeryTimeInput = ctx.formatted;
        this.surgeryTimeState = null;
      }
    },
    onClose() {
      this.$router.push({
        path: "surgicalCases",
      });
    },
    async onSubmit(evt) {
      evt.preventDefault();
      if (moment().startOf("day").isAfter(this.selectedSurgeryDate)) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "Surgery Date is in the past",
        });
        return;
      }
      this.loading = true;
      // 1. Create TPR Object
      var newSurgicalCase = new SurgicalCaseDto();
      newSurgicalCase.division = this.selectedDivision;

      // Branch
      const selectedBranch = this.branchList
        .filter((branch) => branch.value === this.selectedBranch)
        .map((branch) => branch.text)[0];
      var [branchNumber, branchName] = selectedBranch.trim().split(/-(.+)/);
      newSurgicalCase.branchName = branchName.trim();
      newSurgicalCase.branchNumber = branchNumber.trim();

      // Hospital - check that the hospital exists in Makosuite before we continue
      if (this.hospitalListModified.includes(this.selectedHospital)) {
        var [hospitalNumber, hospitalName] = this.selectedHospital
          .trim()
          .split(/-(.+)/);
        newSurgicalCase.hospitalName = hospitalName.trim();
        newSurgicalCase.hospitalNumber = hospitalNumber.trim();
      } else {
        this.$root.$emit("showAlert", {
          type: "error",
          msg:
            "The hospital " +
            this.selectedHospital +
            " does not exist on selected branch in MakoSuite.",
        });
        this.loading = false;
        return;
      }

      // Surgeon - check that the surgeon exists in Makosuite before we continue
      if (this.surgeonListModified.includes(this.selectedSurgeon)) {
        newSurgicalCase.surgeon = new ContactDto();
        var surgeonNumber = this.selectedSurgeon.split(/-(.+)/)[0];
        const surgeon = this.surgeonList.filter(
          (surg) => surgeonNumber.trim() === surg.externalReference
        )[0];
        newSurgicalCase.surgeon = surgeon;
        newSurgicalCase.surgeon.email = surgeonNumber.trim();
      } else {
        this.$root.$emit("showAlert", {
          type: "error",
          msg:
            "The surgeon " +
            this.selectedSurgeon +
            " does not exist on selected branch in MakoSuite.",
        });
        this.loading = false;
        return;
      }

      // Procedure related
      newSurgicalCase.surgeryStartDateTime = moment(
        this.selectedSurgeryDate + " " + this.selectedSurgeryTime
      );
      // Make sure the surgery date time are valid before we continue
      if (this.surgeryDateState == false || this.surgeryTimeState == false) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: "The surgery date or time is in an invalid format.",
        });
        this.loading = false;
        return;
      }
      newSurgicalCase.procedure = this.selectedProcedure;
      newSurgicalCase.operativeSide =
        this.selectedOperativeSide.length > 0
          ? this.selectedOperativeSide[0]
          : null;
      newSurgicalCase.patientId = this.patientId;
      newSurgicalCase.mako = this.mako;
      newSurgicalCase.internalComments = this.internalComments;
      newSurgicalCase.revision = this.revision;

      // Covering Reps
      newSurgicalCase.coveringReps = [];
      this.selectedCoveringReps.forEach((rep) => {
        const coveringRep = this.coveringRepList.filter((user) => {
          if (user.userEmail === rep) return user;
        })[0];
        var contact = new ContactDto();
        contact.firstName = coveringRep.firstName;
        contact.lastName = coveringRep.lastName;
        contact.email = coveringRep.userEmail;
        newSurgicalCase.coveringReps.push(contact);
      });
      var surgicalCaseResponse = null;
      try {
        // 2.1 If case was loaded, update..
        if (this.surgicalCaseId) {
          // first attach treatmentPlanRequests
          newSurgicalCase.treatmentPlanRequests = this.treatmentPlanRequests;
          surgicalCaseResponse = await this.$surgicalCaseApi.surgicalcasesIdPut(
            this.surgicalCaseId,
            newSurgicalCase
          );
          var promises = this.treatmentPlanRequests.map(async (tpr) => {
            let request =
              await this.$treatmentPlanRequestsApi.treatmentplanrequestsGet({
                externalReference: tpr.entityDescriptor,
                includeAcknowledgedRequests: true,
              });
            // Only Update if request is not cancelled
            if (
              request.content[0].metadata.commentedRequestState.requestState !==
              "Cancelled"
            ) {
              // Update surgery date
              request.content[0].treatment.surgeryDate =
                this.selectedSurgeryDate;
              request.content[0].otherContacts = [];
              var primaryContactStillValid = false;
              // Update other contacts
              newSurgicalCase.coveringReps.forEach((rep) => {
                if (rep.email !== request.content[0]?.primaryContact?.email) {
                  request.content[0].otherContacts.push(rep);
                } else {
                  primaryContactStillValid = true;
                }
              });
              if (
                !primaryContactStillValid &&
                (request.content[0].metadata.commentedRequestState
                  .requestState === "Created" ||
                  request.content[0].metadata.commentedRequestState
                    .requestState === "Rejected")
              ) {
                request.content[0].primaryContact = null;
              }
              // Update the TPR with the new surgery date and
              await this.$treatmentPlanRequestsApi.treatmentplanrequestsIdPut(
                request.content[0].metadata.requestId,
                request.content[0].metadata.dateTimeLastModified,
                request.content[0]
              );
            }
          });
          await Promise.all(promises);
          // 2.2 ..or send POST for new Case
        } else {
          surgicalCaseResponse = await this.$surgicalCaseApi.surgicalcasesPost(
            newSurgicalCase
          );
        }
      } catch (e) {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: this.surgicalCaseId
            ? "Failed to update Surgical Case, Error " + e.status
            : "Failed to create Surgical Case, Error " + e.status,
        });
        this.loading = false;
        return;
      }
      this.$root.$emit("showAlert", {
        type: "info",
        msg: this.surgicalCaseId
          ? "Surgical Case updated."
          : "Surgical Case created.",
      });
      this.$router.push({
        path: "surgicalCase",
        query: { id: surgicalCaseResponse.surgicalCaseId },
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~bootstrap/scss/functions", "@/assets/_stryker.scss";

.buttons {
  float: right;
  margin-right: 20px;
}
label,
.col-form-label {
  text-align: left;
  font-size: small;
  color: rgb(95, 95, 95);
}
.jumbotron {
  margin: 2rem 1rem;
}
.red {
  color: $red;
  text-align: left;
}
.custom-checkbox.b-custom-control-lg,
.input-group-lg .custom-checkbox {
  position: absolute;
  bottom: 1rem;
}
</style>